import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { formatDate } from '../lib/dates';
import { api, ApiError } from '../lib/api';
import { ThreadDialog } from './ClassroomPage';
import { useT } from '../context/I18nContext';

/* ============================================================================
   THE FAMILY'S SIDE

   HOMEWORK IS A LIST, NOT A FEED. A parent looking for Tuesday's maths homework
   on Thursday finds it here, grouped by what's due — it doesn't scroll away.

   Ticking it done is a claim it was DONE. Not that it was correct — only the
   teacher can judge that. Conflating those would make the tick worthless.
   ========================================================================== */

interface Homework {
  id: string; classLabel: string; subjectName: string; teacherName: string;
  title: string; details: string;
  assignedOn: string; dueOn: string; attachmentUrl: string | null;
  /** ⚠ Which child this row is for. Always set on the family list. */
  studentId: string | null; studentName: string | null;
  isOverdue: boolean;
  doneByMe: boolean | null; myNote: string | null; wasLate: boolean | null;
}
// ⚠ The API returns `id`, not `studentId` — see PortalStudent in PortalService.
// Declaring it wrong makes the field `undefined` at runtime with no compile error,
// because the response is cast from JSON rather than checked.
interface Child { id: string; fullName: string; admissionNumber: string; classLabel: string | null; }

/** A teacher this family may write to — from the timetable, on the server. */
interface ContactableTeacher { userId: string; name: string; role: string; subjects: string }

/** ⚠ `appended` means the message went into a thread that was already open. Say so, or the parent
    hunts for a new one, does not find it, and sends the whole thing again. */
interface StartResult { conversation: Conversation; appended: boolean }

/**
 * The diary photograph on a homework card.
 *
 * Fetched rather than linked: the endpoint requires the parent's token, and an <img src="/api/...">
 * cannot send one — the browser issues that request itself, without it. So the bytes are fetched
 * here and turned into a blob URL the image tag can use.
 *
 * The blob is revoked on unmount. Without that, a parent scrolling a term of homework leaks one
 * object URL per photo until the tab is closed.
 */
function DiaryPhoto({ homeworkId }: { homeworkId: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    (async () => {
      try {
        const blob = await api.blob(`/api/portal/classroom/homework/${homeworkId}/photo`);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [homeworkId]);

  if (failed) {
    return <p className="muted" style={{ fontSize: 12 }}>The photo could not be loaded.</p>;
  }

  if (!src) {
    return <div className="diary-photo-loading" />;
  }

  return (
    <a href={src} target="_blank" rel="noreferrer" className="diary-photo-link">
      <img src={src} alt="Photo of the homework diary" className="diary-photo" />
      <span className="diary-photo-hint">Tap to open full size</span>
    </a>
  );
}
interface Message {
  id: string; author: string; authorName: string; body: string;
  sentAt: string; readAt: string | null;
}
interface Conversation {
  id: string; studentId: string; studentName: string;
  teacherUserId: string; teacherName: string;
  subject: string; isClosed: boolean;
  startedAt: string; lastMessageAt: string;
  unreadCount: number; messages: Message[];
}

type Tab = 'homework' | 'messages';

export function FamilyClassroomPage() {
  const t = useT();
  const [tab, setTab] = useState<Tab>('homework');

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>{t('School')}</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Homework &amp; messages</h1>
      </div>

      <div className="tab-strip" style={{ gap: 4, marginBottom: 18 }}>
        <TabBtn on={tab === 'homework'} onClick={() => setTab('homework')}>{t('Homework')}</TabBtn>
        <TabBtn on={tab === 'messages'} onClick={() => setTab('messages')}>{t('Messages')}</TabBtn>
      </div>

      {tab === 'homework' && <HomeworkList />}
      {tab === 'messages' && <MessageList />}
    </div>
  );
}

/* ----------------------------------------------------------------- homework */

function HomeworkList() {
  const t = useT();
  const [rows, setRows] = useState<Homework[] | null>(null);
  const [children, setChildren] = useState<Child[]>([]);
  const [showPast, setShowPast] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [childrenFailed, setChildrenFailed] = useState(false);

  const load = useCallback(() => {
    api.get<Homework[]>(`/api/portal/classroom/homework?includePast=${showPast}`)
      .then(setRows).catch(() => setRows([]));
    // ⚠ NOT swallowed. When this failed the list still rendered, the tick box quietly disabled
    // itself, and a parent was left tapping a control that had no reason on screen to be dead.
    api.get<Child[]>('/api/portal/children')
      .then((c) => { setChildren(c); setChildrenFailed(false); })
      .catch(() => setChildrenFailed(true));
  }, [showPast]);
  useEffect(load, [load]);

  const toggle = async (h: Homework, studentId: string) => {
    setError(null);
    try {
      if (h.doneByMe) {
        await api.post(`/api/portal/classroom/homework/${h.id}/undo`, { studentId, note: null });
      } else {
        const note = window.prompt('Anything to tell the teacher? (optional)\n\ne.g. "Couldn\'t finish Q4, will bring tomorrow"');
        if (note === null) return;   // cancelled
        await api.post(`/api/portal/classroom/homework/${h.id}/done`, {
          studentId, note: note.trim() || null,
        });
      }
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not update.');
    }
  };

  // ⚠ The row carries its own child now, so nothing has to be inferred. This used to fall back to
  // "the only child, if there is exactly one", which meant a parent of two saw a dead tick box and
  // no explanation. Kept only as a last resort for a row the server did not stamp.
  const soleChild = children.length === 1 ? children[0].id : null;
  const childOf = (h: Homework) => h.studentId ?? soleChild;

  // Whose homework it is only needs saying when there is more than one child to confuse.
  const showWhose = children.length > 1 || new Set(
    (rows ?? []).map((r) => r.studentId).filter(Boolean),
  ).size > 1;

  if (!rows) {
    return <div className="card" style={{ padding: 20 }}><span className="sk" style={{ width: '50%' }} /></div>;
  }

  const pending = rows.filter((h) => !h.doneByMe);

  return (
    <div>
      {/* ⚠ A dead tick box with no reason on screen is the worst version of this failure. If the
          children could not be loaded the row has nothing to mark, so say so. */}
      {childrenFailed && (
        <div className="card" style={{ padding: 12, marginBottom: 10, fontSize: 13, color: 'var(--ink-2)' }}>
          Could not load your children just now, so homework cannot be ticked off. Reload the page,
          or tell the school if it keeps happening.
        </div>
      )}

      {error && (
        <div className="pill pill-danger" style={{ padding: '9px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>
          {error}
        </div>
      )}

      {pending.length > 0 && (
        <div className="pill pill-warn" style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 16, justifyContent: 'flex-start' }}>
          <strong>{pending.length} {pending.length === 1 ? 'task' : 'tasks'} still to do.</strong>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="empty">
          <h3>{t('No homework')}</h3>
          <p>When a teacher sets homework, it appears here with its due date.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 640 }}>
          {rows.map((h) => (
            <div key={h.id} className="card" style={{
              padding: 16,
              opacity: h.doneByMe ? 0.72 : 1,
              borderLeft: h.isOverdue && !h.doneByMe ? '3px solid var(--brand)' : undefined,
            }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                {/* The tick. One tap. */}
                <button
                  onClick={() => { const sid = childOf(h); if (sid) toggle(h, sid); }}
                  disabled={!childOf(h)}
                  aria-label={h.doneByMe ? 'Mark as not done' : 'Mark as done'}
                  style={{
                    width: 26, height: 26, borderRadius: 7, flexShrink: 0, marginTop: 2,
                    border: `2px solid ${h.doneByMe ? 'var(--ok-ink, #1e7d4f)' : 'var(--line)'}`,
                    background: h.doneByMe ? 'var(--ok-ink, #1e7d4f)' : 'transparent',
                    color: '#fff', fontSize: 14, cursor: childOf(h) ? 'pointer' : 'default',
                    display: 'grid', placeItems: 'center',
                  }}>
                  {h.doneByMe ? '✓' : ''}
                </button>

                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <strong style={{
                      fontSize: 15.5, fontFamily: 'var(--font-display)',
                      textDecoration: h.doneByMe ? 'line-through' : undefined,
                    }}>
                      {h.title}
                    </strong>
                    <span className="pill pill-muted" style={{ fontSize: 10.5 }}>{h.subjectName}</span>
                    {/* ⚠ Only when there is more than one child. With two, the same homework can
                        appear twice — once per child, each with its own tick — and without a name
                        the two rows are indistinguishable and the parent cannot tell which one
                        they just marked. With one child it is noise. */}
                    {showWhose && h.studentName && (
                      <span className="pill" style={{ fontSize: 10.5, fontWeight: 600 }}>
                        {h.studentName}
                      </span>
                    )}
                  </div>

                  <p style={{ fontSize: 13.5, color: 'var(--ink-2)', margin: '5px 0 8px', whiteSpace: 'pre-wrap' }}>
                    {h.details}
                  </p>

                  {/* ⚠ attachmentUrl holds a STORAGE KEY, not a URL — the file is served by an
                      authenticated endpoint that checks this parent is linked to a child in this
                      class. Rendering it as a plain href would 404.

                      Shown inline as an image, because that is what it is: a photograph of the
                      diary. A parent checking tonight's homework wants to look at it, not download
                      a file and find it later. */}
                  {h.attachmentUrl && (
                    <DiaryPhoto homeworkId={h.id} />
                  )}

                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: 12.5,
                      color: h.isOverdue && !h.doneByMe ? 'var(--brand)' : 'var(--ink-3)',
                      fontWeight: h.isOverdue && !h.doneByMe ? 600 : 400,
                    }}>
                      {h.isOverdue && !h.doneByMe ? 'Overdue — ' : 'Due '}
                      {new Date(h.dueOn).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>· {h.teacherName}</span>
                    {h.doneByMe && h.wasLate && (
                      <span className="pill pill-warn" style={{ fontSize: 10 }}>marked late</span>
                    )}
                  </div>

                  {h.myNote && (
                    <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6, fontStyle: 'italic' }}>
                      You told the teacher: “{h.myNote}”
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <button className="btn" style={{ marginTop: 16 }} onClick={() => setShowPast((v) => !v)}>
        {showPast ? 'Hide older homework' : 'Show older homework'}
      </button>
    </div>
  );
}

/* ----------------------------------------------------------------- messages */

function MessageList() {
  const t = useT();
  const [rows, setRows] = useState<Conversation[] | null>(null);
  const [open, setOpen] = useState<Conversation | null>(null);
  const [composing, setComposing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<Conversation[]>('/api/portal/classroom/conversations')
      .then(setRows).catch(() => setRows([]));
  }, []);
  useEffect(load, [load]);

  if (!rows) {
    return <div className="card" style={{ padding: 20 }}><span className="sk" style={{ width: '50%' }} /></div>;
  }

  return (
    <div>
      {/* The same disclosure the teacher sees. Both sides are told. */}
      <div className="card" style={{ padding: 14, marginBottom: 16, borderLeft: '3px solid var(--accent)' }}>
        <strong style={{ fontSize: 13.5 }}>{t('These conversations are part of the school record.')}</strong>
        <p style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 3 }}>
          School administrators can review them.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={() => { setNotice(null); setComposing(true); }}>
          {t('Write to a teacher')}
        </button>
        <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
          {t('Your child’s class teacher, and the teachers who take their subjects.')}
        </span>
      </div>

      {/* ⚠ Shown after the send, because the thing that happened may not be the thing that was
          asked for — an open thread with that teacher is added to rather than duplicated. */}
      {notice && (
        <div className="card" style={{ padding: 12, marginBottom: 14, fontSize: 13, borderLeft: '3px solid var(--ok, var(--accent))' }}>
          {notice}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="empty">
          <h3>{t('No messages')}</h3>
          <p>
            Write to your child’s teacher with the button above — or, if a teacher writes to you,
            the message appears here and you can reply.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 640 }}>
          {rows.map((c) => (
            <button key={c.id} onClick={async () => {
              const full = await api.get<Conversation>(`/api/portal/classroom/conversations/${c.id}`);
              setOpen(full); load();
            }} className="card" style={{
              padding: 14, textAlign: 'left', cursor: 'pointer', width: '100%',
              background: c.unreadCount > 0 ? 'var(--surface-2)' : undefined,
            }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                {c.unreadCount > 0 && (
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--brand)' }} />
                )}
                <strong style={{ fontSize: 15 }}>{c.teacherName}</strong>
                <span style={{ fontSize: 13.5, color: 'var(--ink-2)' }}>— {c.subject}</span>
                <span className="mono" style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)' }}>
                  {formatDate(c.lastMessageAt)}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                About {c.studentName}
              </div>
            </button>
          ))}
        </div>
      )}

      {composing && (
        <ComposeDialog
          onClose={() => setComposing(false)}
          onSent={(r) => {
            setComposing(false);
            setNotice(r.appended
              ? `Your message was added to your open conversation with ${r.conversation.teacherName}.`
              : `Sent to ${r.conversation.teacherName}.`);
            setOpen(r.conversation);
            load();
          }}
        />
      )}

      {open && <ThreadDialog conversation={open} onClose={() => { setOpen(null); load(); }} />}
    </div>
  );
}

/**
 * A parent writing to a teacher for the first time.
 *
 * ⚠ THE TEACHER LIST COMES FROM THE SERVER, PER CHILD, and is re-checked on the way in. This screen
 * cannot offer a member of staff who does not teach the child, and could not message one even if it
 * tried — which is the point: a parent should be able to reach their child's teachers and nobody
 * else in the school.
 */
function ComposeDialog({ onClose, onSent }: { onClose: () => void; onSent: (r: StartResult) => void }) {
  const t = useT();
  const [children, setChildren] = useState<Child[] | null>(null);
  const [studentId, setStudentId] = useState('');
  const [teachers, setTeachers] = useState<ContactableTeacher[] | null>(null);
  const [teacherUserId, setTeacherUserId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<Child[]>('/api/portal/children')
      .then((c) => {
        setChildren(c);
        // ⚠ Pre-selected when there is only one child, because making a parent with one child pick
        // it from a list of one is a step that exists only to be got past.
        if (c.length === 1) setStudentId(c[0].id);
      })
      .catch(() => setChildren([]));
  }, []);

  // ⚠ The teacher list is refetched per child and the selection cleared with it. A teacher who
  // teaches the older child but not the younger one would otherwise stay selected, and the server
  // would refuse a send the screen had made look valid.
  useEffect(() => {
    setTeachers(null); setTeacherUserId('');
    if (!studentId) return;
    let alive = true;
    api.get<ContactableTeacher[]>(`/api/portal/classroom/teachers?studentId=${studentId}`)
      .then((list) => { if (alive) setTeachers(list); })
      .catch(() => { if (alive) setTeachers([]); });
    return () => { alive = false; };
  }, [studentId]);

  const send = async () => {
    if (!studentId || !teacherUserId || !subject.trim() || !body.trim()) return;
    setBusy(true); setError(null);
    try {
      onSent(await api.post<StartResult>('/api/portal/classroom/conversations', {
        studentId, teacherUserId, subject: subject.trim(), message: body.trim(),
      }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The message could not be sent. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const ready = !!studentId && !!teacherUserId && !!subject.trim() && !!body.trim();

  return (
    // ⚠ sheet-backdrop / sheet — the classes this app actually defines. "modal-backdrop" and
    // "modal" are conventional names and are not in any stylesheet here; using them renders the
    // dialog as an unstyled block dumped inline at the bottom of the page, which looks like the
    // screen is broken rather than like a dialog that failed to open.
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{t('Write to a teacher')}</h3>

        {/* The same disclosure as everywhere else this thread is shown. */}
        <p style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: -4 }}>
          {t('School administrators can review these conversations.')}
        </p>

        {children !== null && children.length > 1 && (
          <label className="label" style={{ display: 'block', marginTop: 12 }}>
            {t('About which child')}
            <select className="input" value={studentId} onChange={(e) => setStudentId(e.target.value)}
              style={{ width: '100%', marginTop: 4 }}>
              <option value="">{t('Choose')}</option>
              {children.map((c) => (
                <option key={c.id} value={c.id}>{c.fullName}{c.classLabel ? ` · ${c.classLabel}` : ''}</option>
              ))}
            </select>
          </label>
        )}

        <label className="label" style={{ display: 'block', marginTop: 12 }}>
          {t('Teacher')}
          <select className="input" value={teacherUserId} onChange={(e) => setTeacherUserId(e.target.value)}
            disabled={!studentId || teachers === null} style={{ width: '100%', marginTop: 4 }}>
            <option value="">
              {!studentId ? t('Choose a child first') : teachers === null ? t('Loading…') : t('Choose a teacher')}
            </option>
            {(teachers ?? []).map((x) => (
              <option key={x.userId} value={x.userId}>
                {x.name} — {x.role}{x.subjects ? ` (${x.subjects})` : ''}
              </option>
            ))}
          </select>
        </label>

        {/* ⚠ Not an error, and not silent. A child whose class has no timetable yet has no teachers
            to offer, and a parent staring at an empty dropdown deserves to know why. */}
        {studentId && teachers !== null && teachers.length === 0 && (
          <p style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 6 }}>
            {t('No teachers are listed for this class yet. Please contact the school office.')}
          </p>
        )}

        <label className="label" style={{ display: 'block', marginTop: 12 }}>
          {t('What is it about')}
          <input className="input" value={subject} maxLength={120}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t('A question about maths homework')}
            style={{ width: '100%', marginTop: 4 }} />
        </label>

        <label className="label" style={{ display: 'block', marginTop: 12 }}>
          {t('Your message')}
          <textarea className="input" value={body} rows={5} maxLength={2000}
            onChange={(e) => setBody(e.target.value)}
            style={{ width: '100%', marginTop: 4, resize: 'vertical' }} />
        </label>

        {error && <p style={{ color: 'var(--danger)', fontSize: 13, marginTop: 10 }}>{error}</p>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn" onClick={onClose} disabled={busy}>{t('Cancel')}</button>
          <button className="btn btn-primary" onClick={send} disabled={!ready || busy}>
            {busy ? t('Sending…') : t('Send')}
          </button>
        </div>
      </div>
    </div>
  );
}

function TabBtn({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: 'none', border: 'none', cursor: 'pointer', padding: '8px 14px', fontSize: 14,
      color: on ? 'var(--ink)' : 'var(--ink-3)', fontWeight: on ? 600 : 400,
      borderBottom: on ? '2px solid var(--brand)' : '2px solid transparent',
    }}>{children}</button>
  );
}
